// src/components/aulas/BloqueoForm.tsx
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { TextField, Button, Grid, Box, FormControlLabel, Checkbox } from "@mui/material";

const schema = z.object({
  dow: z.array(z.number().min(0).max(6)).nonempty("Selecciona al menos un día"),
  hora_inicio: z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/, "HH:mm o HH:mm:ss"),
  hora_fin: z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/, "HH:mm o HH:mm:ss"),
  fecha_inicio: z.string(),
  fecha_fin: z.string(),
  motivo: z.string().optional(),
  // ← NUEVO
  permanente: z.boolean().optional(),
});

// Tipo amigable para el form
export type FormData = {
  dow: number[];
  hora_inicio: string;
  hora_fin: string;
  fecha_inicio: string;
  fecha_fin: string;
  motivo?: string;
  permanente?: boolean; // ← NUEVO
};

const DOW = [
  { v: 1, label: "Lun" },
  { v: 2, label: "Mar" },
  { v: 3, label: "Mié" },
  { v: 4, label: "Jue" },
  { v: 5, label: "Vie" },
  { v: 6, label: "Sáb" },
  { v: 0, label: "Dom" }, // backend: 0=domingo
];

export function BloqueoForm({ onSubmit }: { onSubmit: (data: FormData) => void }) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      dow: [],
      hora_inicio: "09:00:00",
      hora_fin: "13:00:00",
      // ← NUEVO
      permanente: false,
    },
  });

  const dowSel = watch("dow");
  const permanente = watch("permanente"); // ← NUEVO
  const toggleDow = (v: number) => {
    const next = dowSel.includes(v) ? dowSel.filter((x) => x !== v) : [...dowSel, v];
    setValue("dow", next, { shouldValidate: true });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div>
            <div style={{ marginBottom: 4 }}>Días de la semana</div>
            <div>
              {DOW.map((d) => (
                <FormControlLabel
                  key={d.v}
                  control={
                    <Checkbox
                      size="small"
                      checked={dowSel.includes(d.v)}
                      onChange={() => toggleDow(d.v)}
                    />
                  }
                  label={d.label}
                />
              ))}
            </div>
            {errors.dow && (
              <div style={{ color: "#d32f2f", fontSize: 12 }}>{errors.dow.message as string}</div>
            )}
          </div>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            size="small"
            label="Hora inicio"
            {...register("hora_inicio")}
            error={!!errors.hora_inicio}
            helperText={(errors.hora_inicio?.message as string) ?? ""}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            size="small"
            label="Hora fin"
            {...register("hora_fin")}
            error={!!errors.hora_fin}
            helperText={(errors.hora_fin?.message as string) ?? ""}
            fullWidth
          />
        </Grid>

        {/* ← NUEVO: Permamente */}
        <Grid item xs={12}>
          <FormControlLabel
            control={<Checkbox {...register("permanente")} checked={!!permanente} />}
            label="Permanente (ignorar fechas)"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            size="small"
            type="date"
            label="Desde"
            InputLabelProps={{ shrink: true }}
            {...register("fecha_inicio")}
            error={!!errors.fecha_inicio}
            fullWidth
            disabled={!!permanente} // opcional
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            size="small"
            type="date"
            label="Hasta"
            InputLabelProps={{ shrink: true }}
            {...register("fecha_fin")}
            error={!!errors.fecha_fin}
            fullWidth
            disabled={!!permanente} // opcional
          />
        </Grid>

        <Grid item xs={12}>
          <TextField size="small" label="Motivo" {...register("motivo")} fullWidth />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end">
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              Guardar bloqueo
            </Button>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
}
